/**
 * cart-extras.js
 *
 * Custom elements that augment the native cart drawer:
 *  - <cart-upsells>: AJAX add-to-cart for upsell tiles.
 *  - <cart-welcome-kit>: reconciles the auto-added welcome kit with cart state.
 *
 * The drawer's existing AJAX flow (assets/cart.js, assets/cart-drawer.js) dispatches
 * a 'cart:updated' event after each successful change. These elements listen for it.
 *
 * SECURITY NOTE: <cart-welcome-kit> is a UX convenience only. Without a server-side
 * Shopify Discount Function, the kit's free price cannot be enforced. See CART_REBUILD.md.
 */

(function () {
  'use strict';

  /**
   * Refresh the visible cart drawer + cart icon by re-rendering their sections.
   * Re-uses the same approach the existing CartItems class uses, so the markup stays in lockstep.
   */
  async function refreshCartDrawer() {
    const sections = ['cart-drawer', 'cart-icon-bubble'];
    const url = `${window.location.pathname}?sections=${sections.join(',')}`;
    const res = await fetch(url);
    if (!res.ok) return;
    const data = await res.json();

    const drawer = document.querySelector('cart-drawer');
    if (drawer && data['cart-drawer']) {
      const newDoc = new DOMParser().parseFromString(data['cart-drawer'], 'text/html');
      const newInner = newDoc.querySelector('#CartDrawer .drawer__inner');
      const oldInner = drawer.querySelector('#CartDrawer .drawer__inner');
      if (newInner && oldInner) oldInner.innerHTML = newInner.innerHTML;
      drawer.classList.toggle('is-empty', newDoc.querySelector('cart-drawer').classList.contains('is-empty'));
    }
    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble && data['cart-icon-bubble']) {
      const newDoc = new DOMParser().parseFromString(data['cart-icon-bubble'], 'text/html');
      const newSection = newDoc.querySelector('.shopify-section');
      if (newSection) bubble.innerHTML = newSection.innerHTML;
    }

    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'cart-extras' } }));
  }

  /* -------- Upsells --------
   * SSR (snippets/cart-upsells.liquid) renders a static fallback list from
   * settings.cart_upsell_products. On connect, we fetch personalized recs
   * from Shopify's Recommendations API (intent=complementary, seeded with
   * the first non-gift cart item) and swap the list if any are returned.
   *
   * The submit handler below is dead code on this element — the
   * document-level capture-phase /cart/add interceptor at the bottom of
   * this file fires first and stopPropagation()'s — but it's kept as a
   * defensive belt for any flow that bypasses the document listener.
   */
  class CartUpsells extends HTMLElement {
    connectedCallback() {
      this.addEventListener('submit', (e) => this.onSubmit(e));
      // Section re-render (after cart change) destroys this element and creates
      // a fresh one with an updated data-primary-product-id, so connectedCallback
      // is the only hook we need — no separate cart:updated listener.
      this.refreshRecommendations();
    }

    async refreshRecommendations() {
      const productId = this.dataset.primaryProductId;
      if (!productId) return; // gifts-only cart, no seed
      if (this._reqInFlight) return;
      this._reqInFlight = true;

      try {
        const url = `/recommendations/products?section_id=cart-recommendations&product_id=${encodeURIComponent(productId)}&intent=complementary&limit=6`;
        const res = await fetch(url, { headers: { Accept: 'text/html' } });
        if (!res.ok) return; // 404 or 5xx — leave SSR fallback
        const html = await res.text();
        if (!html.trim()) return;

        const newDoc = new DOMParser().parseFromString(html, 'text/html');
        const newList = newDoc.querySelector('[data-cart-upsells-list][data-source="recommendations"]');
        if (!newList) return; // section rendered nothing (recommendations.products_count == 0)

        // Filter out tiles whose product is already in the cart. The Recs API
        // doesn't know about cart contents — we extract the handle from each
        // tile's /products/<handle> link and check against /cart.js.
        const inCartHandles = await this._fetchInCartHandles();
        if (inCartHandles.length) {
          [...newList.querySelectorAll('.cart-upsells__item')].forEach((item) => {
            const href = item.querySelector('a.cart-upsells__media')?.getAttribute('href') || '';
            const m = href.match(/\/products\/([^/?#]+)/);
            if (m && inCartHandles.includes(m[1])) item.remove();
          });
        }

        if (newList.children.length === 0) return; // all filtered out — keep static fallback

        const oldList = this.querySelector('[data-cart-upsells-list]');
        if (oldList) oldList.replaceWith(newList);
      } catch (e) {
        console.warn('[cart-upsells] recommendations fetch failed; keeping SSR fallback', e);
      } finally {
        this._reqInFlight = false;
      }
    }

    async _fetchInCartHandles() {
      try {
        const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
        return cart.items.map((i) => i.handle);
      } catch (e) {
        return [];
      }
    }

    async onSubmit(event) {
      const form = event.target.closest('[data-cart-upsell-form]');
      if (!form) return;
      event.preventDefault();
      const button = form.querySelector('button');
      if (button) {
        button.disabled = true;
        button.dataset.originalLabel = button.textContent;
        button.textContent = 'Adding…';
      }
      try {
        const formData = new FormData(form);
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData
        });
        if (!res.ok) throw new Error('add failed');
        await refreshCartDrawer();
      } catch (e) {
        console.warn('[cart-upsells] failed to add', e);
      } finally {
        if (button) {
          button.disabled = false;
          if (button.dataset.originalLabel) button.textContent = button.dataset.originalLabel;
        }
      }
    }
  }
  if (!customElements.get('cart-upsells')) customElements.define('cart-upsells', CartUpsells);

  /* -------- Welcome kit auto-add -------- */
  class CartWelcomeKit extends HTMLElement {
    connectedCallback() {
      this._busy = false;
      this._listener = () => this.reconcile();
      document.addEventListener('cart:updated', this._listener);
      // Reconcile on initial load so the kit gets added even if the user already had a qualifying
      // subscription line item in their cart from a prior session.
      this.reconcile();
    }
    disconnectedCallback() {
      document.removeEventListener('cart:updated', this._listener);
    }
    readState() {
      const node = this.querySelector('[data-state]');
      if (!node) return null;
      try { return JSON.parse(node.textContent); } catch (e) { return null; }
    }
    // Returns the in-flight reconcile promise so callers (e.g. a checkout
    // redirect) can await the auto-add actually finishing instead of racing
    // ahead of it — reconcile() runs off the same cart:updated event they do.
    reconcile() {
      if (this._pending) return this._pending;
      this._pending = this._reconcileImpl().finally(() => {
        this._pending = null;
      });
      return this._pending;
    }
    async _reconcileImpl() {
      // While the supply-swap is mid-transaction (remove 30-day + add 90-day),
      // hold off — the swap's intermediate cart state would otherwise look
      // like "qualifier removed, kit orphaned" and trigger a remove+re-add.
      // The supply-swap dispatches a final cart:updated when it finishes, at
      // which point we reconcile against the settled cart.
      if (window.__fwSwapInFlight) return;

      // Same idea for the straight-to-checkout CTA: it attaches the kit itself
      // against the live cart, because our state blob is rendered server-side
      // and on a landing page describes an empty cart. Once it takes over we
      // must stay out — the blob it left behind still says "no kit line", so
      // reconciling would add a second one.
      if (window.__fwWelcomeKitManaged) return;

      const state = this.readState();
      if (!state) return;

      const desired = state.qualifyingKitVariantIds[0] || null;
      const present = state.welcomeKitLines || [];

      // Remove any existing welcome-kit lines that don't match the desired variant.
      const toRemove = desired ? present.filter((l) => l.variantId !== desired) : present.slice();
      const hasDesired = desired && present.some((l) => l.variantId === desired);
      // The kit is one per order. A cart that reaches us with more than one is
      // the fingerprint of two adders racing (this element vs. the LP buy box's
      // straight-to-checkout attach) — clamp it back rather than leaving the
      // shopper to check out with a doubled gift line.
      const toClamp = desired ? present.filter((l) => l.variantId === desired && l.quantity > 1) : [];

      if (toRemove.length === 0 && toClamp.length === 0 && (!desired || hasDesired)) return; // nothing to do

      try {
        for (const line of toRemove) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: line.key, quantity: 0 })
          });
        }
        for (const line of toClamp) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: line.key, quantity: 1 })
          });
        }
        if (desired && !hasDesired) {
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              id: desired,
              quantity: 1,
              properties: { _welcome_kit: '1' }
            })
          });
        }
        await refreshCartDrawer();
      } catch (e) {
        console.warn('[cart-welcome-kit] reconcile failed', e);
      }
    }
  }
  if (!customElements.get('cart-welcome-kit')) customElements.define('cart-welcome-kit', CartWelcomeKit);

  /* -------- Intercept all /cart/add form submissions, AJAX them, open drawer --------
   * The site has a mix of Dawn <product-form> (already AJAX) and custom Replo / sticky-ATC /
   * inline forms that POST normally and would otherwise redirect through /cart. We intercept
   * at the document level in the capture phase so we win over any form-level handlers.
   *
   * We deliberately skip:
   *  - forms inside <product-form> (Dawn's own handler already AJAXes + opens the drawer)
   *  - forms with `data-skip-cart-extras` opt-out
   *  - forms whose action doesn't match /cart/add
   */
  function isCartAddAction(action) {
    if (!action) return false;
    try {
      const u = new URL(action, window.location.origin);
      return /\/cart\/add(\.js)?$/.test(u.pathname);
    } catch (e) {
      return /\/cart\/add(\.js)?$/.test(action);
    }
  }

  async function ajaxAddToCart(form) {
    const submitButtons = form.querySelectorAll('[type="submit"]');
    submitButtons.forEach((b) => { b.disabled = true; b.classList.add('is-loading'); });
    try {
      const fd = new FormData(form);
      // Ask Shopify to also re-render the cart drawer + icon in the same response.
      fd.set('sections', 'cart-drawer,cart-icon-bubble');
      fd.set('sections_url', window.location.pathname);
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: fd
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        // Surface Shopify's error message in console; don't fall back to a redirect (user would lose context).
        console.warn('[cart-extras] /cart/add.js error', data);
        const msg = data.description || data.message || 'Could not add to cart';
        // Best-effort UI: show alert if no other error surface exists. Leaves user on the page.
        alert(msg);
        return;
      }
      // Apply returned sections (covers cart-drawer + cart-icon-bubble in one round-trip).
      if (data.sections) {
        applySections(data.sections);
      } else {
        await refreshCartDrawer();
      }
      const drawer = document.querySelector('cart-drawer');
      // Honor a one-shot suppression flag set by callers like the post-ATC
      // upsell modal (see window.fwCart in theme.liquid).
      const skipAutoOpen = window.fwCart && window.fwCart.shouldSkipAutoOpen();
      if (drawer && typeof drawer.open === 'function' && !skipAutoOpen) drawer.open();
    } catch (err) {
      console.warn('[cart-extras] ajax add failed', err);
      alert('Could not add to cart. Please try again.');
    } finally {
      submitButtons.forEach((b) => { b.disabled = false; b.classList.remove('is-loading'); });
    }
  }

  function applySections(sections) {
    if (sections['cart-drawer']) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer) {
        const newDoc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
        const newInner = newDoc.querySelector('#CartDrawer .drawer__inner');
        const oldInner = drawer.querySelector('#CartDrawer .drawer__inner');
        if (newInner && oldInner) oldInner.innerHTML = newInner.innerHTML;
        const newDrawer = newDoc.querySelector('cart-drawer');
        if (newDrawer) drawer.classList.toggle('is-empty', newDrawer.classList.contains('is-empty'));
      }
    }
    if (sections['cart-icon-bubble']) {
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble) {
        const newDoc = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
        const newSection = newDoc.querySelector('.shopify-section');
        if (newSection) bubble.innerHTML = newSection.innerHTML;
      }
    }
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'cart-extras-add' } }));
  }

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!isCartAddAction(form.getAttribute('action'))) return;
    if (form.closest('product-form')) return; // Dawn handles it
    if (form.hasAttribute('data-skip-cart-extras')) return;
    event.preventDefault();
    event.stopPropagation();
    ajaxAddToCart(form);
  }, true);

  /* -------- Clear All button -------- */
  document.addEventListener('click', async (event) => {
    const btn = event.target.closest('[data-cart-clear-all]');
    if (!btn) return;
    event.preventDefault();
    btn.disabled = true;
    try {
      await fetch('/cart/clear.js', { method: 'POST', headers: { Accept: 'application/json' } });
      await refreshCartDrawer();
    } catch (e) {
      console.warn('[cart-extras] clear failed', e);
    } finally {
      btn.disabled = false;
    }
  });

  /* -------- Subscribe & save (OTP → subscription) --------
   * Converts a one-time line to a subscription by attaching a selling_plan id to the
   * existing line. The drawer re-renders the line into the Subscription Purchases section.
   */
  document.addEventListener('click', async (event) => {
    const btn = event.target.closest('[data-subscribe-line]');
    if (!btn) return;
    event.preventDefault();
    const key = btn.dataset.lineKey;
    const planId = parseInt(btn.dataset.sellingPlanId, 10);
    if (!key || !planId) return;
    btn.disabled = true;
    btn.style.opacity = '0.5';
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, selling_plan: planId })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        console.warn('[cart-extras] subscribe failed', err);
        return;
      }
      await refreshCartDrawer();
    } catch (e) {
      console.warn('[cart-extras] subscribe error', e);
    } finally {
      btn.disabled = false;
      btn.style.opacity = '';
    }
  });

  /* -------- Close drawer on backdrop click --------
   * Dawn's CartDrawer wires #CartDrawer-Overlay to close, but that element ends up
   * sized 0×0 in our layout, so clicks on the dim area to the left of the panel
   * never hit it. Listen on the document and treat any click outside .drawer__inner
   * (while the drawer is active) as a backdrop click.
   */
  document.addEventListener('click', (event) => {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || !drawer.classList.contains('active')) return;
    // Ignore clicks that originated from the cart icon (which opens the drawer).
    if (event.target.closest('#cart-icon-bubble')) return;
    // If the click is anywhere inside the panel, leave it alone.
    if (event.target.closest('.drawer__inner')) return;
    // If the click came from a tile/button outside the drawer that intentionally manages
    // its own cart logic (e.g. quick-add modal triggers), respect that opt-out.
    if (event.target.closest('[data-cart-keep-open]')) return;
    // If the click is on something inside the drawer's wrapper hierarchy (the dim backdrop
    // area), close. Anything outside cart-drawer entirely is a normal page click.
    if (event.target === drawer || drawer.contains(event.target)) {
      drawer.close?.();
    }
  });

  /* -------- Open drawer when ?viewcart=true is present --------
   * theme.liquid redirects /cart → /?viewcart=true. Non-AJAX add-to-cart
   * forms post to /cart/add → Shopify 302s to /cart → that script bounces
   * to home with ?viewcart=true. We catch it here and open the native
   * drawer.
   */
  function openDrawerFromQueryParam() {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('viewcart') !== 'true') return;
      const drawer = document.querySelector('cart-drawer');
      if (!drawer) return;
      // Strip the param so the drawer doesn't re-open on subsequent navigations / reloads.
      params.delete('viewcart');
      const cleaned = window.location.pathname + (params.toString() ? '?' + params.toString() : '') + window.location.hash;
      window.history.replaceState({}, '', cleaned);
      // Wait for the drawer's connectedCallback so .open() exists.
      requestAnimationFrame(() => {
        if (typeof drawer.open === 'function') drawer.open();
      });
    } catch (e) { /* ignore */ }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', openDrawerFromQueryParam);
  } else {
    openDrawerFromQueryParam();
  }

  /* -------- Wire cart:updated event into the existing cart-drawer flow -------- */
  // The Dawn cart.js / cart-drawer.js doesn't fire its own event after AJAX updates.
  // We monkey-patch fetch on /cart/* endpoints to dispatch a single event after the request resolves.
  const origFetch = window.fetch;
  window.fetch = async function (...args) {
    const result = await origFetch.apply(this, args);
    try {
      const url = typeof args[0] === 'string' ? args[0] : args[0]?.url;
      if (url && /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(url)) {
        // Dispatch via setTimeout so callers can finish their .then chains (including the
        // DOM swaps in cart.js) before listeners run. Microtask scheduling would race the
        // existing CartItems.updateQuantity .then chain.
        setTimeout(() => {
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { url } }));
        }, 0);
      }
    } catch (e) {
      // ignore
    }
    return result;
  };
})();
